import { useMemo } from 'react';
import { useFloraCapabilities } from '../../../../api/agents-client/agent-client.hooks';
import { useGoogleStatus } from '../../../../api/google-integrations-client/google-integrations-client.hooks';
import { useSlackStatus } from '../../../../api/slack-integrations-client/slack-integrations-client.hooks';
import { useTeamsStatus } from '../../../../api/teams-integrations-client/teams-integrations-client.hooks';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { buildConnectionList, type FloraConnectionListItem } from './agent-settings.helpers';
import { FloraConnectionId } from './agent-settings.type';

interface FloraConnections {
  items: FloraConnectionListItem[];
  isLoading: boolean;
  isError: boolean;
  /**
   * Whether the load one connection's row comes from failed: its own status
   * endpoint, or the catalog for catalog-only apps. One integration failing
   * shouldn't take down another's page.
   */
  isConnectionError: (id: string) => boolean;
}

/**
 * Loads every connection Flora can use — the capability catalog's connection
 * buckets plus the Google, Slack, and Teams status endpoints — as one list.
 * Slack and Teams are only queried when their feature flags are on.
 *
 * @returns {FloraConnections} The rows, combined loading and error flags, and a per-connection error check
 */
function useFloraConnections(): FloraConnections {
  const isSlackEnabled = useFeatureFlag(FeatureFlag.FloraSlackIntegration);
  const isTeamsEnabled = useFeatureFlag(FeatureFlag.FloraTeamsIntegration);
  const { capabilities, isLoading: isCatalogLoading, error: catalogError } = useFloraCapabilities();
  const { data: googleStatus, query: googleQuery } = useGoogleStatus();
  const { data: slackStatus, query: slackQuery } = useSlackStatus(isSlackEnabled);
  const { data: teamsStatus, query: teamsQuery } = useTeamsStatus(isTeamsEnabled);

  const items = useMemo(
    () =>
      buildConnectionList({
        capabilities,
        google: googleStatus
          ? { connected: Boolean(googleStatus.connected), email: googleStatus.google_email ?? null }
          : undefined,
        slack: {
          enabled: isSlackEnabled,
          installed: Boolean(slackStatus?.installed),
          userConnected: Boolean(slackStatus?.user_connected),
        },
        teams: {
          enabled: isTeamsEnabled,
          installed: Boolean(teamsStatus?.installed),
          userConnected: Boolean(teamsStatus?.user_connected),
        },
      }),
    [capabilities, googleStatus, isSlackEnabled, isTeamsEnabled, slackStatus, teamsStatus],
  );

  const isConnectionError = (id: string): boolean => {
    switch (id) {
      case FloraConnectionId.Google:
        return googleQuery.isError;
      case FloraConnectionId.Slack:
        return isSlackEnabled && slackQuery.isError;
      case FloraConnectionId.Teams:
        return isTeamsEnabled && teamsQuery.isError;
      default:
        return Boolean(catalogError);
    }
  };

  return {
    items,
    isLoading:
      isCatalogLoading ||
      googleQuery.isLoading ||
      (isSlackEnabled && slackQuery.isLoading) ||
      (isTeamsEnabled && teamsQuery.isLoading),
    isError:
      Boolean(catalogError) ||
      googleQuery.isError ||
      (isSlackEnabled && slackQuery.isError) ||
      (isTeamsEnabled && teamsQuery.isError),
    isConnectionError,
  };
}

export { useFloraConnections };
export type { FloraConnections };
